import { IDynamicContentSlide, SlideDetails } from '@pcp/core/interfaces';
import { replaceSignatureUrls } from '@pcp/core/util';
import { DisplayStateSnapshot } from '../../public/yjs/lesson-ydoc-schema';

/**
 * Re-sign a display-state handoff snapshot's URL-bearing payloads with the
 * importing client's CURRENT Azure Blob SAS.
 *
 * The carried payloads embed a baked-in SAS: the per-layer slide JSON
 * (`imageResource`, `bookpages`, `rubberElementPictures`), each dynamic-content
 * entry, and the `navigationPreview*` payloads (whose JSON literally contains a
 * `sas` field that receivers use to re-sign relative thumbnail parts). Carrying
 * prep-time state verbatim would carry prep-time credentials with it and risk a
 * silent 403 on the beamer — re-signing between export and import restores exact
 * parity with a document written without a handoff.
 *
 * - Slide + dynamic-content JSON is mapped through the existing
 *   `replaceSignatureUrls` (`@pcp/core/util`) — the same helper the
 *   preview-screen effects use when showing a slide. Its URL rewriting is
 *   `in`-guarded at runtime, so the transfer shapes (without `imageVariants` /
 *   `previewpicture`) pass through it safely.
 * - `navigationPreview*` payloads only need their embedded `sas` field replaced:
 *   the thumbnails are stored as relative parts and re-signed on the receive side
 *   from that field (`reverseMapITransferMinimumSlideInformation`).
 *
 * Pure and Angular-free: the caller (the lifecycle effects, which have the Store)
 * reads `fromAuth.selectSas` and supplies `sas`. A field that fails to parse is
 * left unchanged (reported via `onError`) — a carried URL with its original SAS
 * beats a dropped field.
 */
export function resignDisplayStateSnapshotUrls(
    snapshot: DisplayStateSnapshot,
    sas: string,
    onError?: (message: string, err: unknown) => void,
): DisplayStateSnapshot {
    const resignJson = (json: string, resign: (parsed: unknown) => unknown, field: string): string => {
        try {
            const parsed: unknown = JSON.parse(json);
            if (parsed === null || typeof parsed !== 'object') return json;
            return JSON.stringify(resign(parsed));
        } catch (err) {
            onError?.(`resignDisplayStateSnapshotUrls: failed to re-sign ${field} — carried unchanged`, err);
            return json;
        }
    };

    // Runtime rewriting in `replaceSignatureUrls` is `in`-guarded; the cast only
    // satisfies its generic constraint for the transfer shapes stored in the Y.Doc.
    const resignSlide = (parsed: unknown): unknown =>
        replaceSignatureUrls(parsed as SlideDetails, sas);

    const lessonMap: Record<string, unknown> = { ...snapshot.lessonMap };

    for (const key of ['primarySlide', 'secondarySlide', 'tertiarySlide']) {
        const value = lessonMap[key];
        if (typeof value !== 'string') continue;
        lessonMap[key] = resignJson(value, resignSlide, key);
    }

    for (const key of ['navigationPreviewBasicSlides', 'navigationPreviewNextSlides']) {
        const value = lessonMap[key];
        if (typeof value !== 'string') continue;
        lessonMap[key] = resignJson(value, parsed => {
            // Basic-slides payload IS an ITransferMinimumSlideInformationList; the
            // next-slides payload nests one under `extensionSlideList`. Replace the
            // embedded `sas` only where the payload actually carries one (the empty
            // default `{"slides":[]}` has none and stays untouched).
            if ('sas' in (parsed as Record<string, unknown>)) {
                return { ...(parsed as Record<string, unknown>), sas };
            }
            const nested = (parsed as Record<string, unknown>)['extensionSlideList'];
            if (nested !== null && typeof nested === 'object' && 'sas' in nested) {
                return {
                    ...(parsed as Record<string, unknown>),
                    extensionSlideList: { ...(nested as Record<string, unknown>), sas },
                };
            }
            return parsed;
        }, key);
    }

    const resignDynamicContentEntries = (entries: string[]): string[] =>
        entries.map(entryJson => resignJson(
            entryJson,
            parsed => replaceSignatureUrls(parsed as IDynamicContentSlide, sas),
            'dynamicContent entry',
        ));

    return {
        ...snapshot,
        lessonMap,
        dynamicContent: {
            1: resignDynamicContentEntries(snapshot.dynamicContent[1]),
            2: resignDynamicContentEntries(snapshot.dynamicContent[2]),
            3: resignDynamicContentEntries(snapshot.dynamicContent[3]),
        },
    };
}
